import React from 'react';
import { Box, Typography, Button, Container, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { motion } from 'framer-motion'; // Import motion for animations
import PlayCircleFilledOutlinedIcon from '@mui/icons-material/PlayCircleFilledOutlined'; // Import for the play button icon

// SamplePoll component
function SamplePoll() {
  const [selectedValue, setSelectedValue] = React.useState(''); // State for selected poll option
  const [showVideo, setShowVideo] = React.useState(false); // State to control video display

  // Handler for radio button changes
  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  // Handler for poll submission
  const handleSubmit = () => {
    alert(`You selected: ${selectedValue || 'No option selected'}`); // Using alert for simplicity, consider a MUI dialog
    // In a real application, you would send this data to a backend
  };

  return (
    // The ThemeProvider is in App.js, so this component inherits the theme
    <Container component="main" sx={{ flexGrow: 1 }}>
      <Box sx={{
        marginY: '40px',
        padding: '40px',
        backgroundColor: 'rgba(255, 255, 255, 0.8)', // Semi-transparent white background matching homepage
        borderRadius: '12px', // Rounded corners
        boxShadow: '0 8px 16px rgba(0,0,0,0.1)', // Soft shadow
      }}>
        {/* Animated content box for the poll */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <Box className="poll-content" sx={{
            textAlign: 'center',
            maxWidth: '800px', // Constrain width for better readability
            margin: '0 auto',  // Center content horizontally
          }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 900,
                letterSpacing: '0.1em',
                color: 'primary.main', // Using theme primary color
                marginBottom: '20px',
                textTransform: 'uppercase',
                fontFamily: '"Inter", sans-serif"',
                textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              Take Our Sample Poll
            </Typography>

            <Typography
              variant="body1"
              sx={{
                marginBottom: '30px',
                color: 'text.secondary', // Using theme text color
                fontFamily: '"Inter", sans-serif"',
                lineHeight: 1.7,
              }}
            >
              Help us understand public sentiment on important issues. Your feedback matters!
            </Typography>

            {/* Video Section */}
            {/* Removed the heading and body text for the video */}
            <Box sx={{
              position: 'relative',
              paddingBottom: '56.25%', // Maintain 16:9 aspect ratio
              height: 0,
              overflow: 'hidden',
              maxWidth: '100%',
              backgroundColor: '#000',
              margin: '0 auto 30px auto',
              boxShadow: '0 8px 16px rgba(0,0,0,0.1)', // Card shadow
              borderRadius: '12px', // Card border-radius
              background: 'white', // Card background
              display: 'flex', // For centering overlay content
              justifyContent: 'center', // For centering overlay content
              alignItems: 'center', // For centering overlay content
            }}>
              {!showVideo && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(0,0,0,0.7)', // Dark overlay on thumbnail
                    zIndex: 1, // Ensure it's above the iframe (when iframe is hidden)
                  }}
                  onClick={() => setShowVideo(true)}
                >
                  <img
                    src="https://placehold.co/1280x720/000000/FFFFFF?text=Video+Thumbnail" // Placeholder thumbnail
                    alt="Video Thumbnail"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      opacity: 0.6, // Blend thumbnail with overlay
                      borderRadius: '12px', // Rounded corners for thumbnail
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)', // Drop shadow for thumbnail
                    }}
                  />
                  <PlayCircleFilledOutlinedIcon sx={{
                    fontSize: '6rem', // Large play button
                    color: 'white',
                    zIndex: 2, // Ensure play button is above thumbnail
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }} />
                </Box>
              )}
              {showVideo && (
                <iframe
                  src="https://www.youtube.com/embed/k3Xx7xhTE5c?autoplay=1&si=SBXVmupp0E0JWGTl" // Same URL as homepage
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                ></iframe>
              )}
            </Box>

            <Box sx={{ textAlign: 'left', marginBottom: '30px', backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
              <Typography variant="h6" component="h2" sx={{ marginBottom: '15px', color: 'primary.dark', fontWeight: 700 }}>
                Question 1: Should local government prioritize public transportation or road infrastructure development?
              </Typography>
              <RadioGroup
                aria-label="transportation-priority"
                name="transportation-priority"
                value={selectedValue}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="public-transportation"
                  control={<Radio sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.dark' } }} />}
                  label={
                    <Typography variant="body1" sx={{ fontFamily: '"Inter", sans-serif"', fontWeight: 400 }}>
                      Prioritize public transportation (buses, trains, etc.)
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="road-infrastructure"
                  control={<Radio sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.dark' } }} />}
                  label={
                    <Typography variant="body1" sx={{ fontFamily: '"Inter", sans-serif"', fontWeight: 400 }}>
                      Prioritize road infrastructure (highways, bridges, etc.)
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="equal-priority"
                  control={<Radio sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.dark' } }} />}
                  label={
                    <Typography variant="body1" sx={{ fontFamily: '"Inter", sans-serif"', fontWeight: 400 }}>
                      Give equal priority to both
                    </Typography>
                  }
                />
              </RadioGroup>
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={!selectedValue} // Button is disabled if no value is selected
              sx={{
                background: (theme) => theme.palette.primary.main, // Access theme directly for consistent color
                borderRadius: '8px',
                color: 'white',
                padding: '12px 24px',
                fontWeight: 'bold',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  background: (theme) => theme.palette.primary.dark, // Access theme directly
                  opacity: 1,
                  transform: 'scale(1.02)',
                },
                // Styles for disabled state
                '&.Mui-disabled': {
                  background: 'grey !important', // Use !important to override default MUI disabled styles
                  color: 'lightgrey !important',
                  boxShadow: 'none',
                  cursor: 'not-allowed',
                },
              }}
            >
              Submit Poll
            </Button>
          </Box>
        </motion.div>
      </Box>
    </Container>
  );
}

export default SamplePoll;
